import { makeContext } from "@shared/utils/makeContext";
import {
  VideoUIActiveTab,
  VideoUIContentShareZoom,
  VideoUIScrollPositions,
} from "./videoUI.types";

// --- Name ---
const NAME = "VideoUI";

// --- Types ---
export type VideoUIState = {
  isSidebarOpen: boolean;
  activeTab: VideoUIActiveTab;
  contentShareZoom: VideoUIContentShareZoom;
  scrollPositions: VideoUIScrollPositions;
};

export type VideoUIAction =
  | { type: string; payload?: Partial<VideoUIState> }
  | {
      type: string;
      payload?: { scale?: number; translateX?: number; translateY?: number };
    }
  | { type: string; payload?: { tab: VideoUIActiveTab; position: number } };

// --- Constants ---
export const SET_SIDEBAR_OPEN = "videoUI/sidebar/open" as const;
export const SET_SIDEBAR_CLOSE = "videoUI/sidebar/close" as const;
export const TOGGLE_SIDEBAR = "videoUI/sidebar/toggle" as const;
export const SET_ACTIVE_TAB = "videoUI/activeTab/set" as const;
export const SET_CONTENT_SHARE_ZOOM = "videoUI/contentShareZoom/set" as const;
export const RESET_CONTENT_SHARE_ZOOM =
  "videoUI/contentShareZoom/reset" as const;
export const SET_SCROLL_POSITION = "videoUI/sidebar/setScrollPosition" as const;

export const ACTION_TYPES = {
  SET_SIDEBAR_OPEN,
  SET_SIDEBAR_CLOSE,
  SET_ACTIVE_TAB,
  TOGGLE_SIDEBAR,
  SET_CONTENT_SHARE_ZOOM,
  RESET_CONTENT_SHARE_ZOOM,
  SET_SCROLL_POSITION,
} as const;

// --- Initial State ---
export const initialVideoUI: VideoUIState = {
  isSidebarOpen: false,
  activeTab: "roster" as VideoUIActiveTab,
  contentShareZoom: {
    scale: 1,
    translateX: 0,
    translateY: 0,
    isZoomed: false,
  },
  scrollPositions: {
    chat: 0,
    roster: 0,
    materials: 0,
  },
};

// --- Reducer ---
function videoUIReducer(
  state: VideoUIState,
  action: VideoUIAction
): VideoUIState {
  switch (action.type) {
    case SET_SIDEBAR_OPEN:
      return {
        ...state,
        isSidebarOpen: true,
        activeTab:
          (action.payload as Partial<VideoUIState>)?.activeTab ?? "chat",
      };
    case SET_SIDEBAR_CLOSE:
      return {
        ...state,
        isSidebarOpen: false,
      };
    case TOGGLE_SIDEBAR:
      return {
        ...state,
        isSidebarOpen: !state.isSidebarOpen,
      };
    case SET_ACTIVE_TAB:
      return {
        ...state,
        activeTab:
          (action.payload as Partial<VideoUIState>)?.activeTab ?? "chat",
      };
    case SET_CONTENT_SHARE_ZOOM:
      const { scale, translateX, translateY } = action.payload as {
        scale?: number;
        translateX?: number;
        translateY?: number;
      };
      return {
        ...state,
        contentShareZoom: {
          scale: scale ?? state.contentShareZoom.scale,
          translateX: translateX ?? state.contentShareZoom.translateX,
          translateY: translateY ?? state.contentShareZoom.translateY,
          isZoomed: (scale ?? state.contentShareZoom.scale) !== 1,
        },
      };
    case RESET_CONTENT_SHARE_ZOOM:
      return {
        ...state,
        contentShareZoom: {
          scale: 1,
          translateX: 0,
          translateY: 0,
          isZoomed: false,
        },
      };
    case SET_SCROLL_POSITION:
      const { tab, position } = action.payload as {
        tab: VideoUIActiveTab;
        position: number;
      };
      return {
        ...state,
        scrollPositions: {
          ...state.scrollPositions,
          [tab]: position,
        },
      };
    default:
      throw new Error("Unknown action: " + action.type);
  }
}

// --- Context Creation ---
export const {
  Provider: VideoUIProvider,
  useState: useVideoUI,
  useDispatch: useVideoUIDispatch,
} = makeContext<VideoUIState, VideoUIAction>({
  name: NAME,
  initialState: initialVideoUI,
  reducer: videoUIReducer,
});
